// TP-AT (Assignment Timing) -- shared frontend helpers.
// Mirrors the backend `_at_entry_minutes_from_times` contract so
// the UI can pre-compute the same duration locally for instant
// feedback. Backend remains the source of truth on save.

export function hhmmToMinutes(hhmm) {
  if (typeof hhmm !== "string") return null;
  const m = hhmm.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  if (!Number.isFinite(h) || !Number.isFinite(mm)) return null;
  if (h < 0 || h > 23 || mm < 0 || mm > 59) return null;
  return h * 60 + mm;
}

export function minutesToHHMM(mins) {
  if (!Number.isFinite(mins) || mins < 0) return "";
  const m = Math.round(mins) % (24 * 60);
  const h = Math.floor(m / 60);
  const r = m % 60;
  return `${String(h).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

export function addMinutesToHHMM(hhmm, addMins) {
  const base = hhmmToMinutes(hhmm);
  if (base == null) return "";
  return minutesToHHMM(base + (Number(addMins) || 0));
}

// Friendly "2h 30m" / "45m" / "8h" formatter for the duration chip.
export function formatHM(mins) {
  const m = Math.max(0, Math.round(Number(mins) || 0));
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h === 0) return `${r}m`;
  if (r === 0) return `${h}h`;
  return `${h}h ${r}m`;
}

// Compute minutes between two HH:mm times inside an optional
// bounded window, subtracting any portion of a break that overlaps.
// Mirrors backend `_at_entry_minutes_from_times`. Returns null when
// inputs are unusable so callers can keep the existing minutes
// safely.
// TP-P5-overnight (Feb 2026) -- when `end < start` we treat it as
// an overnight entry and add 24h. Same for `shiftEnd < shiftStart`
// (overnight shift envelope). Zero-length (`end === start`) stays
// invalid.
export function entryMinutesFromTimes({
  start,
  end,
  shiftStart = null,
  shiftEnd = null,
  breakMinutes = 0,
  breakStart = null,
}) {
  let s = hhmmToMinutes(start);
  let e = hhmmToMinutes(end);
  if (s == null || e == null || e === s) return null;
  if (e < s) e += 24 * 60;                // overnight entry
  let ws = hhmmToMinutes(shiftStart);
  let we = hhmmToMinutes(shiftEnd);
  const shiftOvernight = (ws != null && we != null && we < ws);
  if (shiftOvernight) {
    we += 24 * 60;                        // overnight shift envelope
    // Entry that looks like it sits before the shift start (e.g.
    // Work 02:00-06:00 inside a 22:00-06:00 shift) actually lives
    // on the "next day" side of the overnight envelope.
    if (s < ws) { s += 24 * 60; e += 24 * 60; }
  }
  if (ws != null) s = Math.max(s, ws);
  if (we != null) e = Math.min(e, we);
  if (e <= s) return 0;
  let duration = e - s;
  const bm = Number(breakMinutes) || 0;
  let bs = hhmmToMinutes(breakStart);
  if (bm > 0 && bs != null) {
    if (shiftOvernight && bs < ws) bs += 24 * 60;
    const be = bs + bm;
    const overlap = Math.max(0, Math.min(e, be) - Math.max(s, bs));
    duration = Math.max(0, duration - overlap);
  }
  return duration;
}

// Detect overlapping assignments within a list of entries. Returns
// an array of [indexA, indexB] pairs (a < b) for any two entries
// whose [start, end] intervals overlap. Entries without both
// start_time and end_time are ignored.
// TP-P5-overnight (Feb 2026) -- overnight entries (`end < start`)
// are normalised to end + 24h before intersecting, so a Work
// 22:00-06:00 block is compared honestly against any other block.
export function findOverlaps(entries) {
  const intervals = [];
  (entries || []).forEach((e, idx) => {
    const s = hhmmToMinutes(e.start_time);
    let en = hhmmToMinutes(e.end_time);
    if (s == null || en == null || en === s) return;
    if (en < s) en += 24 * 60;
    intervals.push({ idx, s, e: en });
  });
  const pairs = [];
  for (let i = 0; i < intervals.length; i += 1) {
    for (let j = i + 1; j < intervals.length; j += 1) {
      const a = intervals[i];
      const b = intervals[j];
      if (a.s < b.e && b.s < a.e) pairs.push([a.idx, b.idx]);
    }
  }
  return pairs;
}
